// using https://github.com/jaredLunde/react-hook/blob/master/packages/resize-observer/src/index.tsx

import React from "react";
import { useCurrentRef } from "./use-current-ref";

export type UseResizeObserverCallback = (
  entry: ResizeObserverEntry,
  observer: ResizeObserver,
) => any;

function createResizeObserver() {
  let ticking = false;
  let allEntries: ResizeObserverEntry[] = [];
  const callbacks: Map<any, Array<UseResizeObserverCallback>> = new Map();
  const observer = new window.ResizeObserver(
    (entries: ResizeObserverEntry[], obs: ResizeObserver) => {
      allEntries = allEntries.concat(entries);
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const triggered = new Set<Element>();
          for (let i = 0; i < allEntries.length; i++) {
            if (triggered.has(allEntries[i].target)) continue;
            triggered.add(allEntries[i].target);
            const cbs = callbacks.get(allEntries[i].target);
            cbs?.forEach((cb) => cb(allEntries[i], obs));
          }
          allEntries = [];
          ticking = false;
        });
      }
      ticking = true;
    },
  );

  return {
    observer,
    subscribe(target: Element, callback: UseResizeObserverCallback) {
      const cbs = callbacks.get(target) ?? [];
      cbs.push(callback);
      observer.observe(target);
      callbacks.set(target, cbs);
    },
    unsubscribe(target: Element, callback: UseResizeObserverCallback) {
      const cbs = callbacks.get(target) ?? [];
      if (cbs.length === 1) {
        observer.unobserve(target);
        callbacks.delete(target);
        return;
      }
      const cbIndex = cbs.indexOf(callback);
      if (cbIndex !== -1) cbs.splice(cbIndex, 1);
      callbacks.set(target, cbs);
    },
  };
}

let resizeObserver: ReturnType<typeof createResizeObserver>;
function getResizeObserver() {
  return resizeObserver
    ? resizeObserver
    : (resizeObserver = createResizeObserver());
}

/**
 * A React hook that fires a callback whenever ResizeObserver detects a change to its size
 *
 * @param target A React ref created by `useRef()` or an HTML element
 * @param callback Invoked with a single `ResizeObserverEntry` any time
 *   the `target` resizes
 */
export function useResizeObserver<T extends Element | null>(
  target: React.MutableRefObject<T>,
  callback: UseResizeObserverCallback,
): ResizeObserver {
  const resizeObserver = getResizeObserver();
  const storedCallback = useCurrentRef(callback);

  React.useEffect(() => {
    let didUnsubscribe = false;
    const targetEl = target && "current" in target ? target.current : target;
    if (!targetEl) return () => {};

    function cb(entry: ResizeObserverEntry, observer: ResizeObserver) {
      if (didUnsubscribe) return;
      storedCallback.current(entry, observer);
    }
    resizeObserver.subscribe(targetEl as Element, cb);

    return () => {
      didUnsubscribe = true;
      resizeObserver.unsubscribe(targetEl as Element, cb);
    };
  }, [target.current, resizeObserver, storedCallback]);

  return resizeObserver.observer;
}
